import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Observable, combineLatest, of } from 'rxjs';
import { Country } from 'src/app/models/country';
import { DocumentService } from 'src/app/services/document/document.service';
import { CountryService } from 'src/app/services/country/country.service';
import { ActivatedRoute } from '@angular/router';
import { SectionBaseline } from 'src/app/models/section-baseline';
import { mergeMap, switchMap, tap, map, startWith } from 'rxjs/operators';
import { MixpanelService } from 'src/app/services/mixpanel/mixpanel.service';
import htmlDocx from 'html-docx-js/dist/html-docx';
import saveAs from 'file-saver';
import Rollbar from 'rollbar';
import { RollbarService } from 'src/app/util/rollbar';

@Component({
  selector: 'app-section-report',
  templateUrl: './section-report.component.html',
  styleUrls: ['./section-report.component.scss']
})
export class SectionReportComponent implements OnInit {

  public selectedCountries: UntypedFormControl;
  public selectedSectionBaselines: UntypedFormControl;
  public selectedFormat: UntypedFormControl;
  public filterCountries$: Observable<Country[]>;
  public filterSectionBaselines$: Observable<SectionBaseline[]>;
  public isSelectionLimitReached$: Observable<boolean>;
  public compareData$: Observable<{ [countryName: string]: { [section: string]: string } }>;
  public showResults$: Observable<boolean> = of(false);
  public columns$: Observable<Country[]>;
  public columnHeaders$: Observable<string[]>;

  constructor(public documentService: DocumentService,
    private countryService: CountryService,
    private activatedRoute: ActivatedRoute,
    @Inject(RollbarService) private rollbar: Rollbar,
    private mixpanelService: MixpanelService) { }

  transformSections(data: any[]) {
    const result = {};

    // Get all unique country codes
    const countryCodes = new Set<string>();
    data.forEach(item => {
      Object.keys(item.row).forEach(code => countryCodes.add(code));
    });

    // Initialize country objects
    countryCodes.forEach(code => {
      result[code] = {};
    });

    // Populate sections for each country
    data.forEach(item => {
      Object.entries(item.row).forEach(([countryCode, content]) => {
        result[countryCode][item.section] = content;
      });
    });

    // Sort sections for each country
    Object.keys(result).forEach(countryCode => {
      const sortedSections = {};
      Object.keys(result[countryCode]).sort().forEach(section => {
        sortedSections[section] = result[countryCode][section];
      });
      result[countryCode] = sortedSections;
    });

    // Transform country codes to names
    const countryCodeToName = {};
    this.filterCountries$.subscribe(countries => {
      countries.forEach(country => {
        countryCodeToName[country.iso2] = country.name;
      });
    });

    // Replace country codes with names in the result object
    const transformedResult = {};
    Object.entries(result).forEach(([countryCode, sections]) => {
      transformedResult[countryCodeToName[countryCode] || countryCode] = sections;
    });

    // Sort by country
    const sortedResult = {};
    Object.keys(transformedResult).sort().forEach(key => {
      sortedResult[key] = transformedResult[key];
    });
    return sortedResult;
  }


  public download() {
    // First check if we have selected values
    if (!this.selectedSectionBaselines.value?.length) {
      console.warn('No sections selected');
      return false;
    }

    // Force emission of current values
    const currentCountries = this.selectedCountries.value;
    const currentSections = this.selectedSectionBaselines.value;

    this.documentService.getCompareSections(currentSections, currentCountries.map(c => c.iso2))
      .pipe(
        map((data) => this.transformSections(data)),
        map((data) => Object.entries(data).map(([country, sections]) =>
          `<h2>${country}</h2>` +
          Object.entries(sections).map(([section, content]) =>
            `<h3>${section}</h3><p>${content}</p>`
          ).join('')
        ))
      ).subscribe(
        (result) => {
          const header = "<!DOCTYPE html>\n<html><head><meta charset='utf-8'></head><body><h1>Country Profile Report</h1>";
          const footer = "</body></html>"
          const sourceHTML = htmlDocx.asBlob(header + result.join('') + footer);
          saveAs(sourceHTML, "Country Profile Report.docx");
          this.mixpanelService.track('Download Country Report');
        },
        (err) => {
          this.rollbar.error("Problem with country report download");
        }
      );

    return false;
  }

  ngOnInit() {
    this.selectedCountries = new UntypedFormControl([]);
    this.selectedSectionBaselines = new UntypedFormControl(null);

    this.filterSectionBaselines$ = this.documentService.getReportableSectionBaselines()
    this.filterCountries$ = this.countryService.userCountries();

    // Add a property to track if selection limit is reached
    this.isSelectionLimitReached$ = this.selectedSectionBaselines.valueChanges.pipe(
      startWith(this.selectedSectionBaselines.value),
      map(selectedValues => selectedValues?.length >= 3),
    );

    this.showResults$ = this.selectedSectionBaselines.valueChanges.pipe(
      startWith(this.selectedSectionBaselines.value),
      map(selectedValues => selectedValues?.length > 0)
    );


    this.compareData$ = combineLatest([
      this.selectedSectionBaselines.valueChanges.pipe(
        startWith(this.selectedSectionBaselines.value)
      ),
      this.selectedCountries.valueChanges.pipe(
        startWith(this.selectedCountries.value)
      )
    ]).pipe(
      map(([sectionBaselines, countries]) => [sectionBaselines, countries.map((c: Country) => c.iso2)]),
      tap(([sectionBaselines, countries]) => this.mixpanelService.track("Run Report",
        {
          "Report Sections": sectionBaselines,
          "Report Countries": countries
        })),
      switchMap(([sectionBaselines, countries]) => this.documentService.getCompareSections(sectionBaselines, countries)),
      map((data) => this.transformSections(data)),
    );
  }

}
